// ─────────────────────────────────────────────────────────────────────
// THE SAVE RULE (#276).
//
// Decided as a product rule: a short, invisible retry is fine; if a save takes
// long enough to become NOTICEABLE, or ultimately FAILS, the member gets a
// human-readable error.
//
// So neither of the two obvious designs. Not "warn and carry on" — a failure is
// always surfaced in the end. And not "block on every refusal" — a blip that
// clears in a few hundred milliseconds should cost the member nothing, not even
// a flash of error.
//
// WHAT COUNTS AS RETRYABLE. Only a failure that says nothing about the request:
// the network, or the server failing to answer. A REFUSAL is an answer — the
// activity is full, the format needs a partner, the session expired — and
// retrying it produces the same refusal three times and a slower error. Those
// surface immediately.
// ─────────────────────────────────────────────────────────────────────

/** A refusal: the server answered, and the answer was no. Never retried. */
export class SaveRefused extends Error {
  readonly status: number;
  readonly code: string | null;
  constructor(message: string, status: number, code: string | null = null) {
    super(message);
    this.name = 'SaveRefused';
    this.status = status;
    this.code = code;
  }
}

export interface SaveWithRetryOptions {
  /** Extra attempts after the first. Default 2 — three tries in all. */
  retries?: number;
  /** Pause before each retry, in ms. Default 150 then 400. */
  backoffMs?: number[];
  /**
   * How long before the member should be told something is happening. Default
   * 400ms — under that, a save that succeeds is invisible, which is the point.
   */
  noticeableAfterMs?: number;
  /** Called once, if the save is still running at `noticeableAfterMs`. */
  onSlow?: () => void;
  /** Injectable for tests; defaults to real timers. */
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
}

const realSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Runs `save`, retrying only what is worth retrying, and reports when it has
 * become noticeable.
 *
 * Resolves with the save's value. Rejects with the last error — a `SaveRefused`
 * carries the server's own reason, which is what the caller shows.
 */
export async function saveWithRetry<T>(
  save: () => Promise<T>,
  opts: SaveWithRetryOptions = {},
): Promise<T> {
  const retries = opts.retries ?? 2;
  const backoff = opts.backoffMs ?? [150, 400];
  const sleep = opts.sleep ?? realSleep;
  const noticeableAfter = opts.noticeableAfterMs ?? 400;

  let slowTimer: ReturnType<typeof setTimeout> | null = null;
  if (opts.onSlow) {
    slowTimer = setTimeout(() => {
      slowTimer = null;
      opts.onSlow?.();
    }, noticeableAfter);
  }

  try {
    let lastError: unknown;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await save();
      } catch (e) {
        lastError = e;
        // An answer is not a blip. Stop immediately and let the caller show it.
        if (e instanceof SaveRefused) throw e;
        if (attempt === retries) break;
        await sleep(backoff[Math.min(attempt, backoff.length - 1)]);
      }
    }
    throw lastError;
  } finally {
    if (slowTimer) clearTimeout(slowTimer);
  }
}
